import {skipToken} from '@reduxjs/toolkit/query';
import {
  Alert,
  AlertVariants,
  Attributes,
  AttributesRow,
  Button,
  openDialog,
  Segment,
  Separator,
} from 'platform/components';
import {Box, Heading, HStack, Link, Show, Text, VStack} from 'platform/foundation';
import {useDateTimeFormatter, useFormatCurrency} from 'platform/locale';
import {match} from 'ts-pattern';

import {useState} from 'react';

import {isNil, isNotNil, prop, uniqBy} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {useGetCustomerPaymentQuery} from '@dms/api/finance';
import {FileMetadata, Payment, useGetCheckoutPaymentsV2Query} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {EMPTY_PLACEHOLDER, useBank, useCashRegister} from '@dms/shared';

import {buildArray, openFile, parseDate, suffixTestId} from 'shared/web';

import {getPdfEmailAttachment} from '../../../utils/getPdfEmailAttachment';
import {SendAfterSalesEmailForm} from '../../SendAftersalesEmailForm/SendAftersalesEmailForm';
import {useNotificationButtonAction} from '../hooks/useNotificationButtonAction';
import type {CheckoutPaymentCardProps} from '../types/CheckoutPaymentCardProps';
import {isCorrectiveTaxDocument} from '../utils/isCorrectiveTaxDocument';
import {CheckoutPaymentActions} from './CheckoutPaymentActions';
import {CheckoutPaymentInvoiceActions} from './CheckoutPaymentInvoiceActions';
import {PaymentDirection} from './PaymentDirection';
import {PaymentsList} from './PaymentsList';

const NOTIFICATION_VARIANT_MAP: Record<'ERROR' | 'WARNING' | 'SUCCESS' | 'INFO', AlertVariants> = {
  ERROR: 'error',
  WARNING: 'warning',
  SUCCESS: 'success',
  INFO: 'info',
};

interface PaymentOverviewProps extends CheckoutPaymentCardProps {
  payment: Payment;
  isLazySaving?: boolean;
}

export function PaymentOverview(props: PaymentOverviewProps) {
  const [selectedSegment, setSegment] = useState('DETAILS');

  const {composeBankAccount} = useBank();
  const {getCashRegisterById} = useCashRegister();
  const formatCurrency = useFormatCurrency();
  const formatDateTime = useDateTimeFormatter();

  const {refetch: refetchCheckoutPayments} = useGetCheckoutPaymentsV2Query({
    checkoutId: props.checkoutId,
  });
  const {handleNotificationButtonClick} = useNotificationButtonAction({
    checkoutId: props.checkoutId,
    paymentId: props.paymentId,
  });

  const {data: customerPayment} = useGetCustomerPaymentQuery(
    isNotNilOrEmpty(props.customerId) ? {customerId: props.customerId} : skipToken
  );

  const customerEmail = customerPayment?.email ?? undefined;
  const customerEmailAddress = isNotNilOrEmpty(customerEmail) ? customerEmail : undefined;

  const paymentData = props.payment;
  const allowedActions = paymentData.uiCapabilities?.allowedActions ?? [];

  const paymentMethod = paymentData.method;
  const paymentMethodLabel = isNil(paymentMethod)
    ? EMPTY_PLACEHOLDER
    : match(paymentMethod)
        .with('BANK_TRANSFER', () => i18n.t('entity.invoice.paymentMethod.bankTransfer'))
        .with('CARD', () => i18n.t('entity.invoice.paymentMethod.card'))
        .with('CASH', () => i18n.t('entity.invoice.paymentMethod.cash'))
        .with('INTERNAL', () => i18n.t('general.labels.internal'))
        .with('CASH_ON_DELIVERY', () => i18n.t('entity.invoice.paymentMethod.cashOnDelivery'))
        .otherwise(() => EMPTY_PLACEHOLDER);

  const handleSendInvoiceDocument = (invoice: FileMetadata) => {
    const serviceCaseId = props.serviceCaseId;

    if (isNil(serviceCaseId)) {
      return;
    }

    const attachment = getPdfEmailAttachment({
      fileId: invoice.fileId,
      fileName: invoice.name,
      fileUrl: invoice.fileUrl,
    });

    openDialog(
      <SendAfterSalesEmailForm
        serviceCaseId={serviceCaseId}
        serviceOrderId={props.serviceOrderId}
        defaultValues={{
          attachments: [attachment],
          customerEmailAddress,
        }}
      />,
      {
        title: i18n.t('general.actions.sendDocument'),
        withAdditionalFooter: true,
        size: 'large',
      }
    );
  };

  const invoiceDocuments = uniqBy(prop('fileId'), [
    ...(paymentData.invoices ?? []),
    ...(paymentData.proformaInvoices ?? []),
    ...(paymentData.internalInvoices ?? []),
  ]);

  const rows = buildArray<AttributesRow>([
    {
      label: i18n.t('entity.checkout.labels.paymentMethod'),
      value: paymentMethodLabel,
      testId: 'paymentMethod',
    },
    {
      label: i18n.t('entity.checkout.labels.issuedOn'),
      value: paymentData.issuedOn
        ? formatDateTime('dateShort', parseDate(paymentData.issuedOn))
        : EMPTY_PLACEHOLDER,
      testId: 'issuedOn',
    },
    {
      label: i18n.t('entity.checkout.labels.dueDate'),
      value: paymentData.dueDate
        ? formatDateTime('dateShort', parseDate(paymentData.dueDate))
        : EMPTY_PLACEHOLDER,
      testId: 'dueDate',
    },
    {
      label: i18n.t('entity.accounting.labels.dateOfTaxableSupply'),
      value: paymentData.dateOfTaxableSupply
        ? formatDateTime('dateShort', parseDate(paymentData.dateOfTaxableSupply))
        : EMPTY_PLACEHOLDER,
      testId: 'dateOfTaxableSupply',
    },
  ])
    .when(paymentMethod === 'CASH', {
      label: i18n.t('entity.checkout.labels.cashRegister'),
      value: paymentData.cashRegisterId
        ? getCashRegisterById(paymentData.cashRegisterId)?.name
        : EMPTY_PLACEHOLDER,
    })
    .when(paymentMethod === 'BANK_TRANSFER', {
      label: i18n.t('general.labels.bankAccount'),
      value: paymentData.bankAccount
        ? composeBankAccount(paymentData.bankAccount)
        : EMPTY_PLACEHOLDER,
    })
    .add({
      label: i18n.t('general.labels.note'),
      value: paymentData.note,
    })
    .whenNotNil(invoiceDocuments, (invoices) =>
      invoices.map((invoice, invoiceIndex) => ({
        label: i18n.t('entity.checkout.labels.invoice'),
        content: (
          <HStack justify="space-between" align="center">
            <Link size="small" onClick={() => openFile(invoice.fileUrl)} title={invoice.name} />
            <HStack spacing={2} align="center">
              <Show when={isNotNil(props.serviceCaseId)}>
                <Link
                  size="small"
                  title={i18n.t('general.actions.sendDocument')}
                  onClick={() => handleSendInvoiceDocument(invoice)}
                  leftIcon="communication/email"
                  data-testid={suffixTestId('invoiceSendDocument', props)}
                />
              </Show>
              <Show when={invoiceIndex === 0}>
                <CheckoutPaymentInvoiceActions
                  allowedActions={allowedActions}
                  checkoutId={props.checkoutId}
                  payment={paymentData}
                  isEditingDisabled={props.isEditingDisabled}
                  isCompletingDisabled={props.isCompletingDisabled}
                  serviceOrderId={props.serviceOrderId}
                  data-testid={suffixTestId('invoiceActions', props)}
                />
              </Show>
            </HStack>
          </HStack>
        ),
        testId: 'invoice',
      }))
    );

  const paymentAmount =
    paymentData.foreignCurrencyPaymentAmount ??
    paymentData.roundedPaymentAmount ??
    paymentData.paymentAmount;

  const remainingPaymentAmount =
    paymentData.remainingForeignCurrencyPaymentAmount ?? paymentData.remainingPaymentAmount;

  return (
    <VStack spacing={4} align="flex-start">
      <Show when={paymentData.documentState === 'request-failed'}>
        <Alert
          variant="error"
          title={i18n.t('entity.checkout.labels.billingDocumentError')}
          hyperlinks={[
            {
              title: i18n.t('general.actions.refresh'),
              onClick: () => refetchCheckoutPayments(),
            },
          ]}
        />
      </Show>
      <Show when={isNotNilOrEmpty(paymentData.notifications)}>
        <VStack spacing={2} width="100%">
          {paymentData.notifications?.map((notification, index) => (
            <Alert
              key={notification.code ?? index}
              variant={NOTIFICATION_VARIANT_MAP[notification.type]}
              title={notification.message}
              hyperlinks={notification.buttons?.map((button) => ({
                title: button.label,
                onClick: () => handleNotificationButtonClick(button),
              }))}
              data-testid={suffixTestId(`notification-${index}`, props)}
            />
          ))}
        </VStack>
      </Show>
      <Show when={isNotNil(paymentAmount)}>
        <HStack spacing={20} width="100%">
          <VStack spacing={1}>
            <Text size="xSmall" color="secondary">
              {i18n.t('general.labels.amount')}
            </Text>
            <Heading size={4} data-testid={suffixTestId('amount', props)}>
              {isNotNil(paymentAmount)
                ? formatCurrency(paymentAmount.amount, paymentAmount.currency, 2)
                : EMPTY_PLACEHOLDER}
            </Heading>
          </VStack>
          <Show
            when={isNotNil(remainingPaymentAmount) && (remainingPaymentAmount?.amount ?? 0) > 0}
          >
            <VStack spacing={1}>
              <Text size="xSmall" color="secondary">
                {i18n.t('entity.invoice.labels.remaining')}
              </Text>
              <Heading size={4} data-testid={suffixTestId('remainingAmount', props)} color="danger">
                {isNotNil(remainingPaymentAmount)
                  ? formatCurrency(
                      remainingPaymentAmount.amount,
                      remainingPaymentAmount.currency,
                      2
                    )
                  : EMPTY_PLACEHOLDER}
              </Heading>
            </VStack>
          </Show>
          <Show when={isCorrectiveTaxDocument(paymentData)}>
            <PaymentDirection
              direction={paymentData.direction}
              data-testid={props['data-testid']}
            />
          </Show>
        </HStack>
      </Show>
      <Separator />
      <VStack spacing={4} justify="flex-start" width="100%">
        <HStack justify="space-between">
          <Box width={75}>
            <Segment
              value={selectedSegment}
              onChange={setSegment}
              options={[
                {label: i18n.t('general.labels.details'), value: 'DETAILS'},
                {
                  label: i18n.t('page.accounting.invoiceDetailPayments.title'),
                  value: 'PAYMENTS',
                },
              ]}
              data-testid={suffixTestId('segment', props)}
            />
          </Box>
          <Show when={isNotNilOrEmpty(paymentData.documentNavigateUrl)}>
            <Button
              variant="link"
              rightIcon="action/open_in_new"
              title={i18n.t('entity.checkout.actions.goToDocument')}
              onClick={() => window.open(paymentData.documentNavigateUrl ?? '', '_blank')}
              data-testid={suffixTestId('goToDocument', props)}
            />
          </Show>
        </HStack>
        <Show when={selectedSegment === 'DETAILS'}>
          <Attributes size="quarter" rows={rows} data-testid={props['data-testid']} />
        </Show>
        <Show when={selectedSegment === 'PAYMENTS'}>
          <PaymentsList
            data-testid={suffixTestId('payments', props)}
            checkoutId={props.checkoutId}
            payment={paymentData}
            customerEmailAddress={customerEmailAddress}
            serviceCaseId={props.serviceCaseId}
            serviceOrderId={props.serviceOrderId}
            isEditingDisabled={props.isEditingDisabled}
            isCompletingDisabled={props.isCompletingDisabled}
          />
        </Show>
      </VStack>
      <Separator spacing={0} />
      <CheckoutPaymentActions
        allowedActions={allowedActions}
        checkoutId={props.checkoutId}
        payment={paymentData}
        isEditingDisabled={props.isEditingDisabled}
        isCompletingDisabled={props.isCompletingDisabled}
        isLazySaving={props.isLazySaving}
        onAddAnotherDepositClick={props.onAddAnotherDepositClick}
        onRemoveDepositClick={props.onRemoveDepositClick}
        contextTarget={props.contextTarget}
        contextId={props.contextId}
        actionContext={props.actionContext}
        serviceOrderId={props.serviceOrderId}
        data-testid={suffixTestId('actions', props)}
      />
    </VStack>
  );
}
